/**
 * PsychoTemplate.tsx — Psychological Portrait.
 * Covers: psycho_profile (both PSYCH and SUMMARY runs).
 *
 * IMPORTANT: your API returns SUMMARY and PSYCH as two INDEPENDENT searches
 * against the same Facebook ID, which is why the old UI showed them as two
 * unrelated popups with different content. This template reads whichever
 * sections are present in the current row and renders them in one view; if
 * you later merge the two runs server-side, nothing here needs to change.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { preparePsychoAnalysysResult } from '../vendor/searcherService'
import {
  Chips, Cols, FieldTable, NoData, Photo, RawCard, ResCard, Stack, Stats, usePhotoLightbox, Verdict, Warn, useTokens,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'

/** Strip the emoji the model appends to almost every field. */
const clean = (s?: string) =>
  String(s ?? '')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

const dangerTone = (s?: string) => {
  const v = clean(s).toLowerCase()
  if (v.startsWith('high')) return 'bad' as const
  if (v.startsWith('medium') || v.startsWith('moderate')) return 'warn' as const
  return 'ok' as const
}

function readRaw(apiData: any) {
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  let summary: any = null
  let psych: any = null
  let image: string | undefined
  entities.forEach((e) => {
    const pa = e?.data?.psychAnalyst
    if (!pa) return
    if (pa.image && !image) image = pa.image
    if (Array.isArray(pa.summaries) && pa.summaries.length) summary = pa.summaries[0]
    if (Array.isArray(pa.profiles) && pa.profiles.length) psych = pa.profiles[0]
  })
  return { summary, psych, image }
}

export function PsychoTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const { summary, psych, image } = React.useMemo(() => readRaw(apiData), [apiData])
  const { openAt, lightbox } = usePhotoLightbox(image ? [image] : [])

  // keep the existing helper available for teams already using it
  const legacy = React.useMemo(() => {
    try { return preparePsychoAnalysysResult(apiData as any) } catch { return null }
  }, [apiData])

  if (!summary && !psych) {
    return <NoData title="No profile returned" hint="The analyst produced no output for this identifier." />
  }

  const traits: string[] = Array.isArray(psych?.predictedCharacteristics) ? psych.predictedCharacteristics : []
  const danger = psych?.levelOfDanger
  const tone = dangerTone(danger)
  const runType = summary && psych ? 'both' : summary ? 'SUMMARY' : 'PSYCH'

  return (
    <>
      {runType !== 'both' && (
        <ResCard>
          <Warn title={`This result is the ${runType} run only`}>
            The API returns <code>SUMMARY</code> and <code>PSYCH</code> as two separate searches against the same
            profile. Run the other one to complete the picture, or merge them server-side.
          </Warn>
        </ResCard>
      )}

      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Name', value: clean(psych?.personName) || clean(summary?.name) || (apiData as any)?.criteria },
            { label: 'Gender', value: clean(summary?.gender) || '—' },
            { label: 'Country', value: clean(summary?.country) || '—' },
            { label: 'Age', value: clean(summary?.age) || 'Not returned', tone: clean(summary?.age) ? undefined : 'warn' },
            { label: 'Occupation', value: clean(summary?.occupation) || 'Unknown', tone: /unknown/i.test(clean(summary?.occupation)) || !summary?.occupation ? 'warn' : undefined },
            ...(danger ? [{ label: 'Danger level', value: clean(danger).split('.')[0], sub: 'model assessment', tone: tone as any }] : []),
          ]}
        />
      </ResCard>

      <Cols n={2}>
        <Stack>
          {danger && (
            <ResCard title="Level of danger" tone={tone === 'ok' ? 'good' : 'accent'}>
              <Verdict
                tone={tone}
                title={clean(danger).split('.')[0] || 'Unknown'}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 6v6c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg>}
              />
              <Typography sx={{ mt: '11px', fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>
                {clean(danger).split('.').slice(1).join('.').trim()}
              </Typography>
            </ResCard>
          )}

          <ResCard title="Source profile">
            <FieldTable
              rows={([
                ['Identifier', (apiData as any)?.criteria],
                ['Name', clean(psych?.personName) || clean(summary?.name)],
                ['Country', clean(summary?.country)],
                ['Occupation', clean(summary?.occupation)],
              ].filter(([, v]) => v) as [string, React.ReactNode][])}
            />
            {image && (
              <Box sx={{ mt: '12px', width: 72, height: 72, borderRadius: '14px', overflow: 'hidden', background: tk.surface3 }}>
                <Photo value={image} onClick={() => openAt(0)} />
              </Box>
            )}
          </ResCard>
        </Stack>

        <Stack>
          {traits.length > 0 && (
            <ResCard title="Predicted characteristics" count={traits.length}>
              <Chips items={traits.map(clean)} tone="b" />
              <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '9px' }}>
                Model-predicted traits, not assessed by a clinician.
              </Typography>
            </ResCard>
          )}

          {psych?.psychologicalPortrait && (
            <ResCard title="Psychological portrait">
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: tk.text2 }}>{clean(psych.psychologicalPortrait)}</Typography>
            </ResCard>
          )}
        </Stack>
      </Cols>

      {summary?.summary && (
        <ResCard title="Summary">
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: tk.text2 }}>{clean(summary.summary)}</Typography>
        </ResCard>
      )}

      <RawCard data={apiData} />
      {lightbox}
    </>
  )
}
